"use client";

import { useEffect } from "react";
import { useRouter } from "next/navigation";
import { Box, Button, Typography } from "@mui/material";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import LandingLayout from "@/layout/landingLayout";

type GlobalErrorProps = {
  error: Error & { digest?: string };
  reset: () => void;
};

export default function GlobalError({ error, reset }: GlobalErrorProps) {
  const router = useRouter();

  useEffect(() => {
    // Log error for debugging/monitoring
    // eslint-disable-next-line no-console
    console.error("Global application error:", error);
  }, [error]);

  const gotoHome = () => {
    router.replace("/");
  };

  return (
    <html lang="en">
      <body>
        <LandingLayout gotoHome={gotoHome}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              px: 2,
            }}
          >
            <Box
              sx={{
                maxWidth: 560,
                width: "100%",
                p: { xs: 3.5, sm: 5 },
                textAlign: "center",
                color: "text.primary",
                borderRadius: 4,
                border: "1px solid rgba(119, 32, 255, 0.2)",
                background: "rgba(12, 12, 16, 0.78)",
                boxShadow: "0 30px 80px rgba(0, 0, 0, 0.55)",
                backdropFilter: "blur(14px)",
              }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 52,
                  height: 52,
                  borderRadius: "50%",
                  border: "1px solid rgba(119, 32, 255, 0.45)",
                  backgroundColor: "rgba(119, 32, 255, 0.12)",
                  mb: 2,
                }}
              >
                <ErrorOutlineRoundedIcon sx={{ fontSize: 28, color: "#C8B2FF" }} />
              </Box>
              <Box
                sx={{
                  height: 2,
                  width: 90,
                  mx: "auto",
                  mb: 2.5,
                  background:
                    "linear-gradient(90deg, rgba(119, 32, 255, 0) 0%, rgba(119, 32, 255, 0.9) 50%, rgba(119, 32, 255, 0) 100%)",
                }}
              />
              <Typography
                variant="h5"
                sx={{ mb: 1, fontWeight: 600, letterSpacing: "-0.02em" }}
              >
                Something went off-script
              </Typography>
              <Typography
                variant="body1"
                sx={{ mb: 3, color: "text.secondary", lineHeight: 1.7 }}
              >
                This page could not be loaded right now. Try again, or head back to
                the dashboard.
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  gap: 1.5,
                }}
              >
                <Button
                  variant="contained"
                  onClick={reset}
                  sx={{
                    textTransform: "none",
                    borderRadius: 999,
                    px: 3,
                    boxShadow: "0 12px 30px rgba(119, 32, 255, 0.35)",
                  }}
                >
                  Try again
                </Button>
                <Button
                  variant="outlined"
                  onClick={gotoHome}
                  sx={{
                    textTransform: "none",
                    borderRadius: 999,
                    px: 3,
                    borderColor: "rgba(255, 255, 255, 0.2)",
                    color: "text.primary",
                    "&:hover": {
                      borderColor: "rgba(255, 255, 255, 0.5)",
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                    },
                  }}
                >
                  Go to Dashboard
                </Button>
              </Box>
            </Box>
          </Box>
        </LandingLayout>
      </body>
    </html>
  );
}
